import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import { Control } from "../../../flatbuffers/generated/control/control";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { VendorFloorIndexing } from "../../../interfaces/Adapter.interface";
import { KEY_FLOOR_INDEXING, KeyFloorIndexing } from "../../../interfaces/Key.interface";
import { toVendorFloorIndexing } from "../../../flatbuffers/api/requestSerializers";

/**
 * The keys tables' sentinel for "no override" — a Select cannot carry `null`
 * as an option value, so the absent case needs a spelling of its own. It is a
 * UI value only and is translated back to `null` before it reaches a payload.
 */
export const FLOOR_INDEXING_INHERIT = "inherit";

/** Every spelling of a floor-indexing override this normaliser will accept. */
const RECOGNISED_FLOOR_INDEXING = new Set<string>([
  KEY_FLOOR_INDEXING.ONE_BASED,
  KEY_FLOOR_INDEXING.ZERO_BASED,
  VendorFloorIndexing[VendorFloorIndexing.ONE_BASED],
  VendorFloorIndexing[VendorFloorIndexing.ZERO_BASED],
]);

/** The same set, as the integers the enum's own members carry. */
const RECOGNISED_FLOOR_INDEXING_VALUES = new Set<number>([
  VendorFloorIndexing.ONE_BASED,
  VendorFloorIndexing.ZERO_BASED,
]);

/**
 * Whether a value names one of the two conventions at all.
 *
 * Numbers are checked against the enum's members rather than waved through:
 * an integer this enum has no member for is not "one-based by default", it is
 * a value nobody set, and the field's absent case is what that has to become.
 */
const isFloorIndexingOverride = (value: unknown): boolean => {
  if (typeof value === "number") return RECOGNISED_FLOOR_INDEXING_VALUES.has(value);
  return RECOGNISED_FLOOR_INDEXING.has(String(value));
};

/**
 * Normalise whatever a hydrated key carries in `floor_indexing` to the wire
 * spelling, or `null` for inherit-from-adapter.
 *
 * A key row is assembled from more than one shape: the FlatBuffer `KeyT`
 * (where the field is a string, but an absent one decodes as `null` on the
 * TypeScript side and as `""` on the Go side), an imported spreadsheet
 * carrying the enum's member name, and — from anything that mistook this
 * field for `Adapter.vendor_floor_indexing` — a `VendorFloorIndexing`
 * integer. Anything else, including the tables' own `FLOOR_INDEXING_INHERIT`
 * sentinel, is the absent case.
 *
 * The integer and member-name spellings resolve through
 * `toVendorFloorIndexing` rather than through a second lookup table, so the
 * two fields keep agreeing about which member means which convention. It is
 * only consulted for values already known to be an override, because it
 * answers `ONE_BASED` for everything it does not recognise — which would turn
 * "inherit", and any integer outside the enum, into a real setting.
 */
export const toKeyFloorIndexing = (value: unknown): KeyFloorIndexing => {
  if (!isFloorIndexingOverride(value)) return null;
  return toVendorFloorIndexing(value as string | number) === VendorFloorIndexing.ZERO_BASED
    ? KEY_FLOOR_INDEXING.ZERO_BASED
    : KEY_FLOOR_INDEXING.ONE_BASED;
};

// CONTROL_SUBTYPES is the row-template list used by the Keys offset
// editor. Sourced from the generated Control enum so a schema change
// (e.g. moving "Up/Down Assigned Hall Call" from Control to Usage)
// flows straight through. The leading "" / "CONTROL" entries are
// intentional placeholders for the editor's "unset" and "category"
// rows and have no enum counterpart.
const CONTROL_SUBTYPES: string[] = [
  "",
  "CONTROL",
  ...Object.entries(Control)
    .filter(([, value]) => typeof value === "number")
    .map(([name]) => name),
];

// USAGE_SUBTYPES exposes the Usage codes that operators map per-car as
// adapter offsets. Usage has ~170 codes total (door open/close, levelling,
// direction, etc.) — most are steady-state signals the adapter emits
// without an explicit offset. Only the four "Assigned Hall Call" codes,
// which moved off Control in the schema bump that ships with this branch,
// are key-mappable today. Sourced from the generated Usage enum so a
// future rename or addition flows straight through.
const USAGE_ASSIGNED_HALL_CALL_KEYS: ReadonlyArray<keyof typeof Usage> = [
  "UpAssignedHallCall",
  "UpAssignedRearHallCall",
  "DownAssignedHallCall",
  "DownAssignedRearHallCall",
];

const USAGE_SUBTYPES: string[] = [
  "",
  "USAGE",
  ...USAGE_ASSIGNED_HALL_CALL_KEYS.filter((name) => typeof Usage[name] === "number"),
];

/**
 * Derive the unit count from an adapter's offset_mapping.
 *
 * Accepts all three shapes the backend may return for `offset_mapping`:
 *   1. Array of entries: `[{unit_number, message_type, offset}, ...]`
 *      — returns `max(unit_number) + 1`.
 *   2. Object-of-arrays keyed by MessageContent:
 *      `{ "1": [offset_unit_0, offset_unit_1, ...], "7": [...] }`
 *      — returns the max inner-array length (legacy dense per-unit form)
 *      or 1 when every inner array has length <= 2 (the new
 *      [initial, initial+delta] shape carries no unit-count signal).
 *   3. Anything else (null, undefined, primitive, malformed) — returns 1.
 *
 * Returning at least 1 prevents downstream `.reduce` / `.length` crashes
 * on non-array input.
 */
export function deriveUnitCount(offsetMapping: unknown): number {
  if (Array.isArray(offsetMapping)) {
    if (offsetMapping.length === 0) return 1;
    const maxUnit = offsetMapping.reduce(
      (mx: number, entry: any) => Math.max(mx, entry?.unit_number ?? 0),
      0,
    );
    return maxUnit + 1;
  }
  if (offsetMapping && typeof offsetMapping === "object") {
    let maxLen = 0;
    Object.values(offsetMapping as Record<string, unknown>).forEach((v) => {
      if (Array.isArray(v) && v.length > maxLen) {
        maxLen = v.length;
      }
    });
    // The new 2-element [initial, initial+delta] shape has length 2 but
    // does NOT represent a 2-unit adapter — treat any length <= 2 as "unknown".
    if (maxLen <= 2) return 1;
    return maxLen;
  }
  return 1;
}

export const updateSpecificKeys = (
  arr1: any[],
  arr2: any[],
  keysToUpdate: { name: string; type: string }[],
) =>
  arr1.map((obj1: any) => {
    const obj2: any = arr2.find((o) => o.idMessage === obj1.idMessage);
    if (obj2) {
      keysToUpdate.forEach((key) => {
        if (key.name in obj2) {
          // eslint-disable-next-line no-param-reassign
          obj1[key.name] = obj2[key.name].toString();
          // eslint-disable-next-line no-param-reassign
          obj1.isValid = true;
        }
      });
    }
    return obj1;
  });

const replaceEmptyStrings = (obj: any) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, typeof value === "boolean" ? false : null]),
  );

export const buildCustomKeysObject = (
  messages: any,
  adapterInterfaceKeys: any,
  protocolType: string,
) =>
  messages.map((msg: any) => ({
    isValid: false,
    idKey: "",
    duplicatedValue: "",
    methodType: "POST",
    idMessage: msg.id,
    message: msg.shortMessage,
    type: msg.message_type ? msg.message_type.name : "",
    idType: msg.messageTypeId,
    encoded_type: msg.messageContentType ?? 2,
    message_value: msg.enumId ?? 0,
    fbs_device_type: msg.deviceType ?? LiftType.ELEVATOR,
    contentType: null,
    lower_encoded_type: null,
    lower_message_content: null,
    lower_message_value: null,
    lower_fbs_device_type: null,
    lower_message_label: null,
    units_based: protocolType.toLocaleLowerCase() === "ln1",
    floors_based: false,
    floor_indexing: null,
    debounce_time: null,
    ...replaceEmptyStrings(adapterInterfaceKeys),
  }));

export const buildAssignedKeysobject = (data: any, keys: any) => {
  const copy = structuredClone(data);
  keys.forEach((item: any) => {
    const index = copy.findIndex(
      (x: any) =>
        x.encoded_type === item.encodedType &&
        x.idType === item.messageContent &&
        x.message_value === item.messageValue &&
        x.fbs_device_type === item.fbsDeviceType,
    );
    if (index >= 0) {
      const target = copy[index];
      target.isValid = true;
      target.idKey = item.id;
      target.methodType = "PUT";
      target.contentType = item.contentType;
      target.lower_encoded_type = item.lowerEncodedType ?? null;
      target.lower_message_content = item.lowerMessageContent ?? null;
      target.lower_message_value = item.lowerMessageValue ?? null;
      target.lower_fbs_device_type = item.lowerFbsDeviceType ?? null;
      // Resolve label for the lower message from the same data array
      if (item.lowerEncodedType != null) {
        const lowerRow = copy.find(
          (r: any) =>
            r.encoded_type === item.lowerEncodedType &&
            r.idType === item.lowerMessageContent &&
            r.message_value === item.lowerMessageValue &&
            r.fbs_device_type === item.lowerFbsDeviceType,
        );
        target.lower_message_label = lowerRow ? lowerRow.message : null;
      } else {
        target.lower_message_label = null;
      }
      target.units_based = item.unitsBased;
      target.floors_based = item.floorsBased;
      target.floor_indexing = toKeyFloorIndexing(item.floorIndexing);
      target.debounce_time = item.debounceTime ?? null;
      if (item.value && typeof item.value === "object") {
        Object.entries(item.value).forEach(([key, value]) => {
          target[key] = value;
        });
      }
    }
  });
  return copy;
};

// eslint-disable-next-line arrow-body-style
export const buildOffsetsTable = (messageTypes: any[], units: number) => {
  return messageTypes.flatMap((messageType) => {
    const baseRow: any = {
      idMessageType: messageType.id,
      isValid: false,
      type: messageType.name,
    };

    for (let i = 0; i < units; i += 1) {
      baseRow[`car_${i}`] = null;
    }

    if (messageType.name.toUpperCase() === "TRAFFIC") {
      const subTypes = [
        "",
        "TRAFFIC",
        "P",
        "CarCall",
        "RearCarCall",
        "HallCall",
        "UpHallCall",
        "UpRearHallCall",
        "DownHallCall",
        "DownRearHallCall",
      ];

      return subTypes.map((subType) => ({
        ...baseRow,
        // eslint-disable-next-line prefer-template
        id: Math.random().toString(36).substring(2, 10) + "-" + Date.now(),
        subType,
      }));
    }

    if (messageType.name.toUpperCase() === "STATUS") {
      const subTypes = ["", "STATUS", "Heartbeat"];

      return subTypes.map((subType) => ({
        ...baseRow,
        // eslint-disable-next-line prefer-template
        id: Math.random().toString(36).substring(2, 10) + "-" + Date.now(),
        subType,
      }));
    }

    if (messageType.name.toUpperCase() === "CONTROL") {
      return CONTROL_SUBTYPES.map((subType) => ({
        ...baseRow,
        // eslint-disable-next-line prefer-template
        id: Math.random().toString(36).substring(2, 10) + "-" + Date.now(),
        subType,
      }));
    }

    if (messageType.name.toUpperCase() === "USAGE") {
      return USAGE_SUBTYPES.map((subType) => ({
        ...baseRow,
        // eslint-disable-next-line prefer-template
        id: Math.random().toString(36).substring(2, 10) + "-" + Date.now(),
        subType,
      }));
    }

    return {
      ...baseRow,
      // eslint-disable-next-line prefer-template
      id: Math.random().toString(36).substring(2, 10) + "-" + Date.now(),
      subType: "",
    };
  });
};

export const buildPreviousOffsets = (offsetsTable: any, previousOffsets: any) => {
  const updatedArray = offsetsTable.map((item: any) => {
    const updatedItem = { ...item };
    previousOffsets.forEach((update: any) => {
      if (item && item.subType && item.sybType !== "") {
        if (update.message_sub_type === item.subType) {
          const key = `car_${update.unit_number}`;
          updatedItem[key] = update.offset;
          updatedItem.isValid = true;
        }
      } else {
        // eslint-disable-next-line no-lonely-if
        if (update.message_type === item.idMessageType) {
          const key = `car_${update.unit_number}`;
          updatedItem[key] = update.offset;
          updatedItem.isValid = true;
        }
      }
    });
    return updatedItem;
  });
  return updatedArray;
};
